import {defineComponent} from "vue";
import {ErrorHandlerQueue} from "@/error/ErrorHandlerQueue";
import {DpExceptionsErrorHandler} from "@/error/handlers/DpExceptionsErrorHandler";
import {AxiosErrorHandler} from "@/error/handlers/AxiosErrorHandler";
import {PageDetailModeEnum} from "@/models/enum/PageDetailModeEnum";
import {UIStateDto} from "@/dtos/UIStateDto";
import {AutoSaveFeedbackStatusEnum} from "@/models/enum/AutoSaveFeedbackStatusEnum";
import {handleSavedSuccessfully} from "@/helpers/toast-helper";
import {AbilityContext} from "@/context/AbilityContext";
import {AuthContext} from "@/context/AuthContext";
import {createConfirmDialog} from "vuejs-confirm-dialog";
import ConfirmModal from "@/components/UI/ConfirmModal.vue";
import {default as Offcanvas} from "bootstrap/js/dist/offcanvas";

export const FormMixin = defineComponent({
    data() {
        return {
            pageUI: UIStateDto.createWithDefaults(),
            offCanvases: new Map() as Map<string, Offcanvas>
        };
    },
    computed: {
        AutoSaveFeedbackStatusEnum() {
            return AutoSaveFeedbackStatusEnum;
        },
        PageDetailModeEnum() {
            return PageDetailModeEnum;
        },
        AbilityContext() {
            return AbilityContext;
        },
        AuthContext() {
            return AuthContext;
        }
    },
    methods: {
        /**
         * This function can be used of more than one form is used using the GenericFormWrapper in the same Vue component.
         *
         * If only one form is used, the submitData, reloadContent, afterSave, and optionally
         * the deleteData and afterDelete should be enough.
         */
        async performGenericSubmit(
            submitFunction: () => Promise<void>,
            afterSaveFunction: () => Promise<void>,
            pageUI?: UIStateDto
        ): Promise<void> {
            if (!this.hasAuthorization()) {
                alert('Not authorized for this action.');
                return;
            }
            const pageUIToUse: UIStateDto = pageUI ?? (this.pageUI as UIStateDto);
            const previousAutoSaveFeedbackStatus = pageUIToUse.getAutoSaveFeedbackStatus();
            pageUIToUse
                .clearError()
                .setSubmitting()
                .setAutoSaveFeedbackStatus(AutoSaveFeedbackStatusEnum.BUSY);
            try {
                await submitFunction();
                pageUIToUse.setAutoSaveFeedbackStatus(AutoSaveFeedbackStatusEnum.NONE);
                await afterSaveFunction();
            } catch (exceptions: any) {
                pageUIToUse.setAutoSaveFeedbackStatus(previousAutoSaveFeedbackStatus);
                this.setExceptions(exceptions, pageUIToUse);
            } finally {
                pageUIToUse.setNotSubmitting();
            }
        },
        /**
         * This function can be used of more than one form is used using the GenericFormWrapper in the same Vue component.
         *
         * If only one form is used, the submitData, reloadContent, afterSave, and optionally
         * the deleteData and afterDelete should be enough.
         */
        async performGenericReload(
            reloadFunction: () => Promise<void>,
            pageUI?: UIStateDto
        ): Promise<void> {
            if (!this.hasAuthorization()) {
                alert('Not authorized for this action.');
                return;
            }
            const pageUIToUse: UIStateDto = pageUI ?? (this.pageUI as UIStateDto);
            pageUIToUse.clearError().setNotReady();
            try {
                await reloadFunction();
                pageUIToUse.setAutoSaveFeedbackStatus(AutoSaveFeedbackStatusEnum.NONE);
            } catch (exceptions: any) {
                this.setExceptions(exceptions, pageUIToUse);
            } finally {
                pageUIToUse.setReady();
            }
        },
        /**
         * This function can be used of more than one form is used using the GenericFormWrapper in the same Vue component.
         *
         * If only one form is used, the submitData, reloadContent, afterSave, and optionally
         * the deleteData and afterDelete should be enough.
         */
        async performGenericDelete(
            deleteFunction: () => Promise<void>,
            afterDeleteFunction: () => Promise<void>,
            pageUI?: UIStateDto,
            showSuccessMessage: boolean = true,
            confirmationMessageKey?: string
        ): Promise<void> {
            if (!this.hasAuthorization()) {
                alert('Not authorized for this action.');
                return;
            }
            const {reveal, onConfirm} = createConfirmDialog(ConfirmModal, {
                title: this.$t('delete'),
                body: this.$t(confirmationMessageKey ?? 'deleteConfirmation'),
                confirmButtonText: this.$t('delete'),
                confirmButtonClass: 'btn-danger',
            });

            onConfirm(async () => {
                const pageUIToUse: UIStateDto = pageUI ?? (this.pageUI as UIStateDto);
                const previousAutoSaveFeedbackStatus = pageUIToUse.getAutoSaveFeedbackStatus();
                pageUIToUse.setDeleting().clearError().setAutoSaveFeedbackStatus(AutoSaveFeedbackStatusEnum.BUSY);
                try {
                    await deleteFunction();
                    pageUIToUse.setAutoSaveFeedbackStatus(AutoSaveFeedbackStatusEnum.NONE);
                    if (showSuccessMessage) {
                        await handleSavedSuccessfully(this.$t('deletedSuccessfully'));
                    }
                    await afterDeleteFunction();
                } catch (exceptions: unknown) {
                    pageUIToUse.setAutoSaveFeedbackStatus(previousAutoSaveFeedbackStatus);
                    this.setExceptions(exceptions, pageUIToUse);
                } finally {
                    pageUIToUse.setNotDeleting();
                }
            });

            await reveal();
        },
        /**
         * This function can be used of more than one form is used using the GenericFormWrapper in the same Vue component.
         *
         * If only one form is used, the submitData, reloadContent, afterSave, and optionally
         * the deleteData and afterDelete should be enough.
         */
        async performGenericDeleteItemWithKey(
            itemKey: string,
            deleteFunction: (_key: string) => Promise<void>,
            afterDeleteFunction: () => Promise<void>,
            pageUI?: UIStateDto,
            showSuccessMessage: boolean = true,
            confirmationMessageKey?: string
        ): Promise<void> {
            if (!this.hasAuthorization()) {
                alert('Not authorized for this action.');
                return;
            }
            const {reveal, onConfirm} = createConfirmDialog(ConfirmModal, {
                title: this.$t('delete'),
                body: this.$t(confirmationMessageKey ?? 'deleteConfirmation'),
                confirmButtonText: this.$t('delete'),
                confirmButtonClass: 'btn-danger',
            });

            onConfirm(async () => {
                const pageUIToUse: UIStateDto = pageUI ?? (this.pageUI as UIStateDto);
                const previousAutoSaveFeedbackStatus = pageUIToUse.getAutoSaveFeedbackStatus();
                pageUIToUse.setItemDeleting(itemKey).clearError().setAutoSaveFeedbackStatus(AutoSaveFeedbackStatusEnum.BUSY);
                try {
                    await deleteFunction(itemKey);
                    pageUIToUse.setAutoSaveFeedbackStatus(previousAutoSaveFeedbackStatus);
                    if (showSuccessMessage) {
                        await handleSavedSuccessfully(this.$t('deletedSuccessfully'));
                    }
                    await afterDeleteFunction();
                } catch (exceptions: unknown) {
                    pageUIToUse.setAutoSaveFeedbackStatus(previousAutoSaveFeedbackStatus);
                    this.setExceptions(exceptions, pageUIToUse);
                } finally {
                    pageUIToUse.removeItemDeleting(itemKey);
                }
            });

            await reveal();
        },
        async submitData(): Promise<void> {
            await this.performGenericSubmit(this.doSubmit, this.afterSave);
        },
        async initialize(): Promise<void> {
            if (!this.hasAuthorization()) {
                alert('Not authorized for this action.');
                return;
            }
            this.pageUI.clearError().setNotReady();
            try {
                await this.doInitialize();
                await this.doReload();
                this.pageUI.setAutoSaveFeedbackStatus(AutoSaveFeedbackStatusEnum.NONE);
            } catch (exceptions: any) {
                this.setExceptions(exceptions, this.pageUI as UIStateDto);
            } finally {
                this.pageUI.setReady();
            }
        },
        async reloadContent(): Promise<void> {
            await this.performGenericReload(this.doReload);
        },
        async deleteData(): Promise<void> {
            await this.performGenericDelete(this.doDelete, this.afterDelete);
        },
        async deleteItemWithKey(itemKey: string): Promise<void> {
            await this.performGenericDeleteItemWithKey(itemKey, this.doDeleteItemWithKey, this.afterDelete);
        },
        async afterSave(message?: string): Promise<void> {
            await handleSavedSuccessfully(message);
            await this.reloadContent();
        },
        async afterDelete(): Promise<void> {
            await this.reloadContent();
        },
        async doNothing(): Promise<void> {
            // Empty function. Can be used in case an afterSave, afterDelete or other function is not required.
        },
        onFormDataChanged(): void {
            this.pageUI.setAutoSaveFeedbackStatus(AutoSaveFeedbackStatusEnum.UNSAVED_CHANGES);
        },
        setExceptions(exceptions: unknown, pageUI?: UIStateDto): void {
            const pageUIToUse: UIStateDto = pageUI ?? (this.pageUI as UIStateDto);
            ErrorHandlerQueue.create()
                .add(DpExceptionsErrorHandler.createWithDefaultUIStateBehavior(pageUIToUse))
                .add(AxiosErrorHandler.createWithDefaultUIStateBehavior(pageUIToUse))
                .catch(exceptions, true);
            window.scrollTo(0, 0);
       },
        async doSubmit(): Promise<void> {
            throw new Error('doSubmit must be implemented');
        },
        async doReload(): Promise<void> {
            throw new Error('doReload must be implemented');
        },
        async doInitialize(): Promise<void> {
            throw new Error('doInitialize must be implemented');
        },
        async doDelete(): Promise<void> {
            throw new Error('doReload must be implemented if delete functionality is required');
        },
        async doDeleteItemWithKey(_itemKey: string): Promise<void> {
            throw new Error('doReload must be implemented if delete functionality is required');
        },
        hasAuthorization(): boolean {
            return true;
        },
        closeModal(modalId: string): void {
            const modal = document.getElementById(modalId);
            const closeButton = modal?.querySelector('[data-bs-dismiss="modal"]');
            if (closeButton) {
                (closeButton as HTMLElement).click();
            }
        },
        openOffCanvas(offCanvasId: string): void {
            let offCanvas = this.offCanvases.get(offCanvasId);
            if (!offCanvas) {
                offCanvas = new Offcanvas('#' + offCanvasId);
                this.offCanvases.set(offCanvasId, offCanvas);
            }
            offCanvas.show();
        },
        /**
         * This function closes the offcanvas with the given id, or, of no id is supplied, the closest offcanvas.
         * @param offCanvasId
         */
        closeOffCanvas(offCanvasId: string | null = null): void {
            let offcanvasEl: HTMLElement | null = null;

            if (offCanvasId) {
                offcanvasEl = document.getElementById(offCanvasId);
            } else {
                offcanvasEl = (this.$el as HTMLElement)?.closest('.offcanvas');
            }

            if (offcanvasEl) {
                const closeButton = offcanvasEl.querySelector('[data-bs-dismiss="offcanvas"]');
                if (closeButton) {
                    (closeButton as HTMLElement).click();
                }
            }
        }
    },
});
